import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { FiExternalLink, FiX } from 'react-icons/fi';
import './Portfolio.css';

const PORTFOLIO_API = 'https://portal.roolink.net/modules/addons/roolink_portfolio/api.php';

const fallbackItems = [
  {
    id: 1, title: 'E-Commerce Platform', title_ar: 'منصة تجارة إلكترونية', category: 'web',
    image: 'https://images.unsplash.com/photo-1661956602116-aa6865609028?w=600&h=400&fit=crop',
    description: 'Full-featured online store with payment integration',
    description_ar: 'متجر إلكتروني متكامل مع تكامل الدفع',
    tags: ['React', 'Node.js', 'Stripe']
  },
  {
    id: 2, title: 'Healthcare Mobile App', title_ar: 'تطبيق رعاية صحية', category: 'mobile',
    image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&h=400&fit=crop',
    description: 'Patient management and telemedicine application',
    description_ar: 'تطبيق إدارة المرضى والطب عن بُعد',
    tags: ['Flutter', 'Firebase']
  },
  {
    id: 3, title: 'Corporate Dashboard', title_ar: 'لوحة تحكم مؤسسية', category: 'web',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop',
    description: 'Analytics dashboard with real-time data visualization',
    description_ar: 'لوحة تحليلات مع تصور بيانات فوري',
    tags: ['React', 'D3.js', 'AWS']
  },
  {
    id: 4, title: 'Restaurant Branding', title_ar: 'هوية مطعم', category: 'design',
    image: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=600&h=400&fit=crop',
    description: 'Complete brand identity and digital presence',
    description_ar: 'هوية علامة تجارية كاملة وحضور رقمي',
    tags: ['UI/UX', 'Branding']
  },
  {
    id: 5, title: 'Delivery App', title_ar: 'تطبيق توصيل', category: 'mobile',
    image: 'https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?w=600&h=400&fit=crop',
    description: 'Real-time tracking delivery application',
    description_ar: 'تطبيق توصيل مع تتبع فوري',
    tags: ['Flutter', 'Google Maps']
  },
  {
    id: 6, title: 'SaaS Platform', title_ar: 'منصة SaaS', category: 'web',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop',
    description: 'Subscription-based project management tool',
    description_ar: 'أداة إدارة مشاريع بالاشتراك',
    tags: ['React', 'PostgreSQL', 'Stripe']
  },
];

const Portfolio = () => {
  const { t, i18n } = useTranslation();
  const [filter, setFilter] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const isAr = i18n.language === 'ar';

  useEffect(() => {
    fetch(PORTFOLIO_API)
      .then(res => res.json())
      .then(data => {
        if (data.success && data.data && data.data.length > 0) {
          setItems(data.data);
        } else {
          setItems(fallbackItems);
        }
      })
      .catch(() => setItems(fallbackItems))
      .finally(() => setLoading(false));
  }, []);

  const getTitle = (item) => (isAr && item.title_ar) ? item.title_ar : item.title;
  const getDesc = (item) => (isAr && item.description_ar) ? item.description_ar : item.description;

  const filters = ['all', 'web', 'mobile', 'design'];
  const filtered = filter === 'all' ? items : items.filter(item => item.category === filter);

  return (
    <section id="portfolio" className="section portfolio-section">
      <div className="container text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="section-tag">{t('portfolio.sectionTag')}</span>
          <h2 className="section-title">{t('portfolio.title')}</h2>
          <p className="section-subtitle">{t('portfolio.subtitle')}</p>
        </motion.div>

        <motion.div
          className="portfolio-filters"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {filters.map((f) => (
            <button
              key={f}
              className={`filter-btn ${filter === f ? 'active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {t(`portfolio.${f}`)}
            </button>
          ))}
        </motion.div>

        <motion.div className="portfolio-grid" layout>
          <AnimatePresence mode="popLayout">
            {filtered.map((item) => (
              <motion.div
                key={item.id}
                className="portfolio-card"
                layout
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.4 }}
                whileHover={{ y: -10 }}
                onClick={() => setSelectedItem(item)}
              >
                <div className="portfolio-img-wrapper">
                  <img src={item.image} alt={getTitle(item)} loading="lazy" />
                  <div className="portfolio-overlay">
                    <FiExternalLink size={24} />
                    <span>{t('portfolio.viewProject')}</span>
                  </div>
                </div>
                <div className="portfolio-info">
                  <h3>{getTitle(item)}</h3>
                  <p>{getDesc(item)}</p>
                  <div className="portfolio-tags">
                    {(Array.isArray(item.tags) ? item.tags : []).map((tag) => (
                      <span key={tag} className="portfolio-tag">{tag}</span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedItem && (
          <motion.div
            className="portfolio-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedItem(null)}
          >
            <motion.div
              className="portfolio-modal"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button className="modal-close" onClick={() => setSelectedItem(null)}>
                <FiX size={24} />
              </button>
              <img src={selectedItem.image} alt={getTitle(selectedItem)} />
              <div className="modal-info">
                <h3>{getTitle(selectedItem)}</h3>
                <p>{getDesc(selectedItem)}</p>
                {selectedItem.project_url && (
                  <a href={selectedItem.project_url} target="_blank" rel="noopener noreferrer" className="portfolio-link">
                    <FiExternalLink /> {t('portfolio.viewProject')}
                  </a>
                )}
                <div className="portfolio-tags">
                  {(Array.isArray(selectedItem.tags) ? selectedItem.tags : []).map((tag) => (
                    <span key={tag} className="portfolio-tag">{tag}</span>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Portfolio;
